// Puntos de venta de F&B de UN hotel, al final de su ficha: lo de hoy y lo que
// llevan acumulado en el mes.
//
// 🔴 POR QUÉ ESTÁ AQUÍ Y NO EN UNA PANTALLA APARTE. Patricio: «cuando presionamos
// en el hotel, en primera instancia nos muestra esto; sería muy interesante que
// podamos desplegar para arriba con el dedo y nos muestre los puntos de F&B,
// cuánto han facturado cada uno y cuánto llevan acumulado en el mes». La ficha ya
// es una hoja que desliza, así que el bloque va al final: se descubre con el dedo
// sin cambiar de pantalla ni pedir otro toque.
//
// 🔴 DE DÓNDE SALE, QUE ES LA PARTE DELICADA. De `noray_facturacion` — la misma
// tabla que Informes › Noray, punto de venta parseado del PMS. Es la capa del
// MES EN CURSO de la doctrina sellada en docs/08: provisional y al día. NO es el
// F&B del P&L de Control de Gestión, que es la fuente buena del mes CERRADO y la
// que cuadra al euro con Hotelos. Los dos números no van a coincidir y ninguno
// está mal: son dos capas distintas. Por eso la tarjeta lo dice en su pie, en vez
// de dejar que alguien los compare y saque conclusiones.
//
// Y la agregación contable por dimensión —la que enseñaba presupuestos contra
// huecos y por la que los outlets salieron de la demo— NO se usa aquí. Aquello
// estaba casi vacío en el mes en curso (Valle Romano sin ningún punto, La Piazza
// a cero, el Pool Bar del Ritual también, mientras Noray sí los tenía).
import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { api, type HotelDiaLive, type HotelRow } from '@/api/client';
import { C, F } from '@/constants/theme';
import { fmt, fmtE } from '@/lib/format';
import { Chip } from './ui';

export default function PuntosVenta({ hotel }: { hotel: HotelRow }) {
  const [dato, setDato] = useState<HotelDiaLive | null>(null);
  const [motivo, setMotivo] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vivo = true;
    setCargando(true); setDato(null); setMotivo(null);
    if (hotel.id == null) { setCargando(false); setMotivo('Este hotel no tiene ficha de facturación.'); return; }
    api.hotelDia(hotel.id).then((r) => {
      if (!vivo) return;
      if (r.ok) setDato(r.dato); else setMotivo(r.motivo);
      setCargando(false);
    });
    return () => { vivo = false; };
  }, [hotel.id]);

  const titulo = <Text style={s.lbl}>PUNTOS DE VENTA · F&B</Text>;

  if (cargando) {
    return <View style={s.caja}>{titulo}<Text style={s.txt}>Leyendo los folios (cuenta huésped)…</Text></View>;
  }

  /* 🔴 «NO LOS TIENE» NO ES LO MISMO QUE «ESTÁ A CERO». `noray_facturacion` es de
     Noray: un hotel de OnaSystem, Navision o Avalon no va a tener puntos NUNCA, y
     enseñarle un hueco al director de ese hotel se lee como que su F&B no vendió
     nada. Se dice cuál es el motivo y de qué PMS es. */
  if (dato && dato.publicaPuntos === false) {
    return (
      <View style={s.caja}>
        {titulo}
        <Text style={s.txt}>
          {`Este hotel no publica puntos de venta: su PMS es ${hotel.pms.toUpperCase()} y los folios (cuenta huésped) `
            + 'por punto solo llegan de Noray. No es que estén a cero, es que no existen todavía.'}
        </Text>
      </View>
    );
  }

  if (!dato) {
    return <View style={s.caja}>{titulo}<Text style={s.txt}>{motivo ?? 'No se pudo leer la facturación.'}</Text></View>;
  }

  const pos = dato.pos ?? [];
  if (!pos.length) {
    return (
      <View style={s.caja}>
        {titulo}
        <Text style={s.txt}>Este hotel no tiene ningún punto de venta con facturación este mes.</Text>
      </View>
    );
  }

  const mesTotal = dato.subPos?.mes ?? pos.reduce((a, p) => a + (p.mes ?? 0), 0);
  const hoyTotal = dato.subPos?.sinIva ?? 0;

  return (
    <View style={s.caja}>
      {titulo}
      <View style={s.cab}>
        <Text style={[s.cabTxt, { flex: 1 }]}>punto</Text>
        <Text style={[s.cabTxt, s.colHoy]}>{dato.esHoy ? 'hoy' : 'el día'}</Text>
        <Text style={[s.cabTxt, s.colMes]}>mes</Text>
      </View>
      {pos.map((p) => (
        <View key={p.punto} style={s.fila}>
          <Text style={s.nom} numberOfLines={1}>{p.punto}</Text>
          {/* Un punto cerrado hoy con mes acumulado enseña «—», no un 0: el 0 se
              lee como «no vendió» y lo que pasa es que hoy no ha abierto. */}
          <Text style={[s.hoy, !p.sinIva && { color: C.faint }]}>
            {p.sinIva ? fmtE(p.sinIva) : '—'}
          </Text>
          <Text style={s.mes}>{p.mes ? fmtE(p.mes) : '—'}</Text>
        </View>
      ))}
      <View style={s.total}>
        <Text style={s.totalTxt}>
          {`${fmt(pos.length)} punto${pos.length === 1 ? '' : 's'} · ${fmtE(mesTotal)} en el mes`}
        </Text>
        <Text style={s.subTxt}>
          {hoyTotal ? `${fmtE(hoyTotal)} ${dato.esHoy ? 'hoy' : 'ese día'}` : 'sin facturación de F&B hoy'}
          {dato.ultSync ? ` · última sync ${dato.ultSync}` : ''}
        </Text>
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7, marginTop: 9 }}>
        <Chip>provisional</Chip>
        <Chip>folios de Noray (cuenta huésped)</Chip>
        <Chip>neto sin IVA</Chip>
      </View>
      {/* 🔴 El pie que evita la conversación equivocada: alguien va a comparar
          esto con el F&B del P&L y no va a cuadrar. Se dice antes de que pase. */}
      <Text style={s.pie}>
        Facturación de Noray del mes en curso, sin la recepción. Es provisional: el F&B
        que cuadra al euro con Hotelos es el del P&L de Control de Gestión, y ese
        llega cuando se publica el mes. No son el mismo número y ninguno está mal.
      </Text>
    </View>
  );
}

const s = StyleSheet.create({
  caja: { marginTop: 16, borderTopColor: C.line, borderTopWidth: 1, paddingTop: 13 },
  lbl: { fontFamily: F.uiBold, fontSize: 10, color: C.faint, letterSpacing: 1.1, marginBottom: 9 },
  txt: { fontFamily: F.ui, fontSize: 11.5, color: C.muted, lineHeight: 16 },
  cab: { flexDirection: 'row', alignItems: 'flex-end', paddingBottom: 5 },
  cabTxt: { fontFamily: F.ui, fontSize: 9, color: C.faint, textTransform: 'uppercase', letterSpacing: 0.5 },
  colHoy: { width: 84, textAlign: 'right' },
  colMes: { width: 92, textAlign: 'right' },
  fila: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, borderTopColor: C.line, borderTopWidth: 1 },
  nom: { flex: 1, fontFamily: F.uiMed, fontSize: 12, color: C.ink, paddingRight: 8 },
  hoy: { width: 84, textAlign: 'right', fontFamily: F.ui, fontSize: 11.5, color: C.muted, fontVariant: ['tabular-nums'] },
  mes: { width: 92, textAlign: 'right', fontFamily: F.uiBold, fontSize: 12, color: C.ink, fontVariant: ['tabular-nums'] },
  total: { backgroundColor: C.card2, borderRadius: 10, padding: 10, marginTop: 10 },
  totalTxt: { fontFamily: F.uiMed, fontSize: 12.5, color: C.ink },
  subTxt: { fontFamily: F.ui, fontSize: 11, color: C.muted, marginTop: 2 },
  pie: { fontFamily: F.ui, fontSize: 10, color: C.faint, marginTop: 10, lineHeight: 14 },
});
